import React from "react";
import Card from "components/card";
import { useLanguage } from "context/LanguageContext";
import {
  MdCheckCircle,
  MdClose,
  MdError,
  MdNavigateBefore,
  MdNavigateNext,
  MdRefresh,
  MdVisibility,
} from "react-icons/md";
import {
  auditLogService,
  type AuditLogListParams,
} from "services/systemadmin/auditLogService";
import type { AuditEventItem } from "types/domain";
import ActionButton from "components/ui/ActionButton";

const PAGE_SIZE = 20;

function JsonBlock({ value }: { value: Record<string, unknown> | null }) {
  if (!value || Object.keys(value).length === 0)
    return <span className="text-ink-subtle">—</span>;
  return (
    <pre className="max-h-56 overflow-auto rounded-lg bg-surface-muted p-3 text-xs text-ink-muted">
      {JSON.stringify(value, null, 2)}
    </pre>
  );
}

export default function AuditLogs() {
  const { locale, t } = useLanguage();
  const [items, setItems] = React.useState<AuditEventItem[]>([]);
  const [total, setTotal] = React.useState(0);
  const [page, setPage] = React.useState(1);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [selected, setSelected] = React.useState<AuditEventItem | null>(null);
  const [filters, setFilters] = React.useState({
    actorEmail: "",
    actionType: "",
    entityType: "",
    result: "ALL",
    dateFrom: "",
    dateTo: "",
  });
  const [appliedFilters, setAppliedFilters] = React.useState(filters);

  const loadLogs = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    const params: AuditLogListParams = {
      skip: (page - 1) * PAGE_SIZE,
      limit: PAGE_SIZE,
      actor_email: appliedFilters.actorEmail.trim() || undefined,
      action_type: appliedFilters.actionType.trim() || undefined,
      entity_type: appliedFilters.entityType.trim() || undefined,
      success:
        appliedFilters.result === "ALL"
          ? undefined
          : appliedFilters.result === "SUCCESS",
      date_from: appliedFilters.dateFrom
        ? new Date(`${appliedFilters.dateFrom}T00:00:00`).toISOString()
        : undefined,
      date_to: appliedFilters.dateTo
        ? new Date(`${appliedFilters.dateTo}T23:59:59.999`).toISOString()
        : undefined,
    };
    try {
      const response = await auditLogService.list(params);
      setItems(response.items);
      setTotal(response.total);
    } catch (err) {
      setItems([]);
      setTotal(0);
      setError(err instanceof Error ? err.message : t("audit.loadError"));
    } finally {
      setLoading(false);
    }
  }, [appliedFilters, page, t]);

  React.useEffect(() => {
    void loadLogs();
  }, [loadLogs]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const formatDate = (value: string) =>
    new Intl.DateTimeFormat(locale, {
      dateStyle: "short",
      timeStyle: "medium",
    }).format(new Date(value));

  const updateFilter = (key: keyof typeof filters, value: string) =>
    setFilters((current) => ({ ...current, [key]: value }));

  const applyFilters = (event: React.FormEvent) => {
    event.preventDefault();
    setPage(1);
    setAppliedFilters(filters);
  };

  const clearFilters = () => {
    const empty = {
      actorEmail: "",
      actionType: "",
      entityType: "",
      result: "ALL",
      dateFrom: "",
      dateTo: "",
    };
    setFilters(empty);
    setAppliedFilters(empty);
    setPage(1);
  };

  const inputClass =
    "h-10 rounded-lg border border-line bg-surface px-3 text-sm text-ink outline-none focus:border-brand-500";

  return (
    <div className="mt-3 space-y-4">
      <div>
        <h1 className="text-2xl font-bold text-ink">
          {t("audit.title")}
        </h1>
        <p className="mt-1 text-sm text-ink-muted">{t("audit.subtitle")}</p>
      </div>

      <Card extra="p-4">
        <form
          onSubmit={applyFilters}
          className="grid gap-3 md:grid-cols-2 xl:grid-cols-6"
        >
          <input
            className={inputClass}
            value={filters.actorEmail}
            onChange={(e) => updateFilter("actorEmail", e.target.value)}
            placeholder={t("audit.actorEmail")}
          />
          <input
            className={inputClass}
            value={filters.actionType}
            onChange={(e) => updateFilter("actionType", e.target.value)}
            placeholder={t("audit.actionType")}
          />
          <input
            className={inputClass}
            value={filters.entityType}
            onChange={(e) => updateFilter("entityType", e.target.value)}
            placeholder={t("audit.entityType")}
          />
          <select
            className={inputClass}
            value={filters.result}
            onChange={(e) => updateFilter("result", e.target.value)}
          >
            <option value="ALL">{t("audit.allResults")}</option>
            <option value="SUCCESS">{t("audit.success")}</option>
            <option value="FAILED">{t("audit.failed")}</option>
          </select>
          <input
            className={inputClass}
            type="date"
            aria-label={t("audit.dateFrom")}
            value={filters.dateFrom}
            onChange={(e) => updateFilter("dateFrom", e.target.value)}
          />
          <input
            className={inputClass}
            type="date"
            aria-label={t("audit.dateTo")}
            value={filters.dateTo}
            onChange={(e) => updateFilter("dateTo", e.target.value)}
          />
          <div className="flex gap-2 md:col-span-2 xl:col-span-6 xl:justify-end">
            <ActionButton
              type="button"
              variant="secondary"
              onClick={clearFilters}
            >
              {t("audit.clear")}
            </ActionButton>
            <ActionButton type="submit" variant="primary">
              {t("audit.filter")}
            </ActionButton>
          </div>
        </form>
      </Card>

      <Card extra="overflow-hidden">
        <div className="flex items-center justify-between border-b border-line p-4">
          <span className="text-sm text-ink-muted">
            {t("audit.total")}:{" "}
            <strong className="text-ink">{total}</strong>
          </span>
          <ActionButton
            variant="ghost"
            size="sm"
            onClick={() => void loadLogs()}
            title={t("audit.refresh")}
          >
            <MdRefresh className="h-5 w-5" />
          </ActionButton>
        </div>
        {error ? (
          <div className="m-4 rounded-lg bg-red-50 p-4 text-sm text-red-700 dark:bg-red-500/10 dark:text-red-300">
            {error}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[900px] text-left">
              <thead className="bg-surface-muted text-xs uppercase text-ink-muted">
                <tr>
                  {[
                    "time",
                    "actor",
                    "actionType",
                    "entity",
                    "result",
                    "sourceIp",
                    "details",
                  ].map((key) => (
                    <th key={key} className="px-4 py-3 font-semibold">
                      {t(`audit.${key}`)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-line">
                {loading ? (
                  <tr>
                    <td
                      colSpan={7}
                      className="px-4 py-12 text-center text-sm text-ink-muted"
                    >
                      {t("audit.loading")}
                    </td>
                  </tr>
                ) : items.length === 0 ? (
                  <tr>
                    <td
                      colSpan={7}
                      className="px-4 py-12 text-center text-sm text-ink-muted"
                    >
                      {t("audit.empty")}
                    </td>
                  </tr>
                ) : (
                  items.map((item) => (
                    <tr
                      key={item.id}
                      className="text-sm text-ink-muted hover:bg-surface-hover"
                    >
                      <td className="whitespace-nowrap px-4 py-3">
                        {formatDate(item.occurred_at)}
                      </td>
                      <td className="px-4 py-3">
                        <div className="font-medium">
                          {item.actor_email || t("audit.system")}
                        </div>
                        <div className="text-xs text-ink-subtle">
                          {item.actor_role
                            ? t(`enum.role.${item.actor_role}`)
                            : "—"}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="rounded-md bg-brand-50 px-2 py-1 text-xs font-semibold text-brand-600 dark:bg-brand-500/10">
                          {item.action_type}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div>{item.entity_type}</div>
                        <div
                          className="max-w-[160px] truncate text-xs text-ink-subtle"
                          title={item.entity_id}
                        >
                          {item.entity_id}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        {item.success ? (
                          <span className="inline-flex items-center gap-1 text-green-600 dark:text-green-300">
                            <MdCheckCircle />
                            {t("audit.success")}
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-red-600 dark:text-red-300">
                            <MdError />
                            {t("audit.failed")}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">{item.source_ip || "—"}</td>
                      <td className="px-4 py-3">
                        <ActionButton
                          variant="ghost"
                          size="sm"
                          onClick={() => setSelected(item)}
                          title={t("audit.viewDetails")}
                          className="text-brand-500 hover:bg-brand-50 dark:hover:bg-white/10"
                        >
                          <MdVisibility className="h-5 w-5" />
                        </ActionButton>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
        <div className="flex items-center justify-between border-t border-line p-4 text-sm">
          <span className="text-ink-muted">
            {t("audit.page")} {page}/{totalPages}
          </span>
          <div className="flex gap-2">
            <ActionButton
              variant="ghost"
              size="sm"
              disabled={page === 1 || loading}
              onClick={() => setPage((value) => value - 1)}
              className="border border-line"
            >
              <MdNavigateBefore />
            </ActionButton>
            <ActionButton
              variant="ghost"
              size="sm"
              disabled={page === totalPages || loading}
              onClick={() => setPage((value) => value + 1)}
              className="border border-line"
            >
              <MdNavigateNext />
            </ActionButton>
          </div>
        </div>
      </Card>

      {selected && (
        <div
          className="bg-black/50 fixed inset-0 z-50 flex items-center justify-center p-4"
          onClick={() => setSelected(null)}
        >
          <div
            className="max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-2xl bg-surface p-6 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-5 flex items-center justify-between">
              <h2 className="text-xl font-bold text-ink">
                {t("audit.detailTitle")} #{selected.id}
              </h2>
              <ActionButton
                variant="ghost"
                size="sm"
                onClick={() => setSelected(null)}
              >
                <MdClose />
              </ActionButton>
            </div>
            <dl className="mb-5 grid gap-3 text-sm md:grid-cols-2">
              <div>
                <dt className="text-ink-subtle">{t("audit.requestId")}</dt>
                <dd className="break-all text-ink">
                  {selected.request_id || "—"}
                </dd>
              </div>
              <div>
                <dt className="text-ink-subtle">{t("audit.userAgent")}</dt>
                <dd className="break-all text-ink">
                  {selected.user_agent || "—"}
                </dd>
              </div>
              <div>
                <dt className="text-ink-subtle">{t("audit.reason")}</dt>
                <dd className="text-ink">
                  {selected.reason || "—"}
                </dd>
              </div>
              <div>
                <dt className="text-ink-subtle">{t("audit.errorMessage")}</dt>
                <dd className="text-red-600 dark:text-red-300">
                  {selected.error_message || "—"}
                </dd>
              </div>
            </dl>
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <h3 className="mb-2 text-sm font-semibold text-ink">
                  {t("audit.beforeState")}
                </h3>
                <JsonBlock value={selected.before_state} />
              </div>
              <div>
                <h3 className="mb-2 text-sm font-semibold text-ink">
                  {t("audit.afterState")}
                </h3>
                <JsonBlock value={selected.after_state} />
              </div>
              <div className="md:col-span-2">
                <h3 className="mb-2 text-sm font-semibold text-ink">
                  {t("audit.changedFields")}
                </h3>
                <JsonBlock value={selected.changed_fields} />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
